import { FC } from 'react';
import Icon, { IconName, IconProps } from './Icon';

/**
 * Drop-in replacements for the react-icons components the app used, drawn with
 * Hugeicons. Same names and props (size, color, title, className, style), so
 * call sites only change their import. Prefer <Icon name="…" /> in new code.
 */
export interface ReactIconProps extends Omit<IconProps, 'name' | 'label'> {
  /** react-icons' filled icons took their colour from fill. */
  fill?: string;
  title?: string;
}

const as = (name: IconName): FC<ReactIconProps> => {
  const Component: FC<ReactIconProps> = ({ size = '1em', width, height, color, fill, title, ...rest }) => (
    <Icon name={name} size={width ?? height ?? size} color={color ?? fill} label={title} {...rest} />
  );
  Component.displayName = name;
  return Component;
};

export const AiOutlineExport = as('export');
export const AiOutlineFileText = as('document');
export const BiCommentEdit = as('commentEdit');
export const BsBook = as('book');
export const BsFilter = as('filter');
export const BsInfoCircle = as('info');
export const BsPatchCheck = as('verified');
export const CiHashtag = as('hashtag');
export const FaAngleDoubleLeft = as('chevronsLeft');
export const FaAngleDoubleRight = as('chevronsRight');
export const FaArrowCircleUp = as('arrowUp');
export const FaArrowUp = as('arrowUp');
export const FaCheck = as('check');
export const FaChevronLeft = as('chevronLeft');
export const FaChevronRight = as('chevronRight');
export const FaExclamationTriangle = as('warning');
export const FaMapLocationDot = as('map');
export const FaPencil = as('edit');
export const FaQuestionCircle = as('help');
export const FaRegFolder = as('folder');
export const FaSailboat = as('vessel');
export const FaShieldAlt = as('shield');
export const FaShoppingCart = as('cart');
export const FaTimes = as('close');
export const FaUpload = as('upload');
export const FaUserCheck = as('userCheck');
export const FaUserGear = as('userSettings');
export const FaUserTie = as('officer');
export const FaX = as('close');
export const GiRank3 = as('rank');
export const GrContact = as('contact');
export const HiMiniChevronDown = as('chevronDown');
export const HiMiniChevronRight = as('chevronRight');
export const HiMiniChevronUp = as('chevronUp');
export const HiMiniChevronUpDown = as('sort');
export const HiMiniQrCode = as('qrCode');
export const HiOutlineLocationMarker = as('location');
export const HiPencil = as('edit');
export const MdBlock = as('blocked');
export const MdChevronLeft = as('chevronLeft');
export const MdDeleteForever = as('delete');
export const MdError = as('error');
export const MdErrorOutline = as('error');
export const MdFormatBold = as('bold');
export const MdFormatItalic = as('italic');
export const MdFormatListBulleted = as('bulletList');
export const MdFormatListNumbered = as('numberedList');
export const MdHistory = as('history');
export const MdImage = as('image');
export const MdLink = as('link');
export const MdLinkOff = as('unlink');
export const MdOutlineCancel = as('cancelled');
export const MdOutlineCheckCircle = as('success');
export const MdOutlineDeleteSweep = as('delete');
export const MdOutlineDone = as('check');
export const MdOutlineEmail = as('email');
export const MdOutlineForum = as('forum');
export const MdOutlineInfo = as('info');
export const MdOutlinePending = as('pending');
export const MdOutlinePendingActions = as('tasks');
export const MdOutlineWarningAmber = as('warning');
export const MdPayment = as('payment');
export const MdPersonRemove = as('userRemove');
export const MdRestore = as('restore');
export const MdTitle = as('heading');
export const PiProhibitBold = as('blocked');
export const TbArrowBackUp = as('reply');
export const TbArrowForward = as('forward');
export const TbCircleCheck = as('success');
export const TbClipboardCheck = as('taskDone');
export const TbCornerUpLeft = as('reply');
export const TbDotsVertical = as('moreVertical');
export const TbDownload = as('download');
export const TbEdit = as('edit');
export const TbEngine = as('engine');
export const TbExternalLink = as('externalLink');
export const TbEye = as('show');
export const TbFileText = as('document');
export const TbFileTypePdf = as('pdf');
export const TbFileTypePng = as('png');
export const TbFileTypeXls = as('spreadsheet');
export const TbHistory = as('history');
export const TbLock = as('lock');
export const TbLockOpen = as('unlock');
export const TbPencil = as('edit');
export const TbRefresh = as('refresh');
export const TbSailboat = as('vessel');
export const TbSettings = as('settings');
export const TbTrash = as('delete');
export const TbUserPlus = as('userAdd');
export const TbX = as('close');
